import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import i18next from 'i18next';
import { useDispatch, useSelector } from 'react-redux';

import Table from '@material-ui/core/Table';
import { TableHead } from '@material-ui/core';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import { makeStyles } from '@material-ui/core/styles';
import TablePagination from '@material-ui/core/TablePagination';

import { setPortal } from 'modules/portal';
import { makeHeadData, getHeadObj, makeRowColumn } from 'lib/commonLib';
import { targetingCode } from 'lib/api/common';
import { adexMediaStatistics, getAdexUserList } from 'lib/api/adex';

import CurrentStatus from 'components/atoms/CurrentStatus';
import FilterWrap from 'components/atoms/FilterWrap';
import MbTooltip from 'components/atoms/MbTooltip';
import MbButton from 'components/atoms/MbButton';
import OutlinedButton from 'components/atoms/OutlinedButton';
import Popover from 'components/molecules/Popover';
import IconAreaWrap from 'components/molecules/MbTable/IconAreaWrap';
import BasicTable from 'components/molecules/MbBasicTable';
import MbSelectbox from 'components/molecules/MbSelectbox';
import StatisticsCommonFilter from 'components/organisms/StatisticsCommonFilter';

import MemoModal from './MemoModal';
import AdexSearchBox from './AdexSearchBox';

const useStyles = makeStyles((theme) => ({
  root: {
    // 테이블 th, td 커스텀 css
    '& th.mb-ad,& th.mb-cw, & th.mb-hu, & th.mb-ad': {
      backgroundColor: '#d2d2d2',
    },
    '& th.mb-widerplanet': {
      backgroundColor: '#078AD8',
    },
    '& th.mb-ekbeatle': {
      backgroundColor: '#FF4444',
    },
    '& th.mb-criteo': {
      backgroundColor: '#FDCC08',
    },
    '& th.mb-adoplow': {
      backgroundColor: '#6A5ACD',
    },
    '& th.mb-toast': {
      backgroundColor: '#9c3851',
    },
    '& th.mb-shoppul123': {
      backgroundColor: '#AFFFBA',
    },
    '& th.mb-adop': {
      backgroundColor: '#2DB400',
    },
    '& th.mb-dable1': {
      backgroundColor: '#2EFEF7',
    },
    '& th.mb-fullscreen': {
      backgroundColor: '#81F7F3',
    },
    '& th.mb-phaserepddn': {
      backgroundColor: '#81F7F3',
    },
    '& th.mb-mediamixer': {
      backgroundColor: '#F6D8CE',
    },
    '& th.mb-adfit': {
      backgroundColor: '#F3E2A9',
    },
    '& th.mb-adexinter': {
      backgroundColor: '#A5DF00',
    },
    '& th.mb-exelbid': {
      backgroundColor: '#F5BCA9',
    },
    '& th.mb-iwmgg': {
      backgroundColor: '#CEF6F5',
    },
    '& th.mb-adinmobi': {
      backgroundColor: '#c65cff',
    },
    '& th.mb-manplus': {
      backgroundColor: '#e8f70f',
    },
    '& th.mb-adfitsdk': {
      backgroundColor: '#00daa8',
    },
    '& th.mb-admixersdk': {
      backgroundColor: '#FF0099',
    },
    '& th.mb-criteosdk': {
      backgroundColor: '#ffa75a',
    },
    '& th.mb-total:': {
      borderTop: 'solid 2px #000',
    },
    '& td.mb-total:': {
      borderLeft: 'solid 2px #000',
      borderRight: 'solid 2px #000',
    },
    '& tbody > tr:nth-child(2):': {
      backgroundColor: '#f3f3f3',
      color: '#179702',
    },
  },
  table: {
    adexParEprsCnt: {
      fontWeight: 'bold',
    },
  },
  parInfoBox: {
    '& div': {
      marginTop: 20,
      marginBottom: 20,
    },
  },
  siteName: {
    color: theme.palette.common.black,
    fontWeight: 'bold',
  },
  accountStr: {
    color: 'red',
    fontWeight: 'bold',
  },
  memo: {
    color: 'blue',
  },
  icoBox: {
    display: 'flex',
    alignItems: 'center',
    padding: '8px 0',
    marginRight: 5,
    fontSize: 15,
    color: '#4d5059',
    cursor: 'pointer',
    '& > button': {
      marginRight: 5,
    },
    '& > p': {
      margin: 0,
    },
  },
  tableCellAlign: {
    display: 'flex',
    '& > p': {
      width: '100%',
    },
  },
}));

let initialization = false;
let initParam = {
  sDate: moment().add(-11, 'day').format('YYYYMMDD'),
  eDate: moment().add(-1, 'day').format('YYYYMMDD'),
  advrtsPrdtCode: '01',
  exlFlag: 'Y',
  page: 1,
  limit: 10,
};

const filterList = [
  {
    CODE_ID: '01',
    CODE_DESC: i18next.t('배너'),
  },
  {
    CODE_ID: '07',
    CODE_DESC: i18next.t('네이티브'),
  },
];

const exlFlagList = [
  {
    label: i18next.t('전체'),
    value: '00',
  },
  {
    label: i18next.t('연동'),
    value: 'Y',
  },
  {
    label: i18next.t('비연동'),
    value: 'N',
  },
];

const sortTpCodeList = [
  {
    label: i18next.t('전체'),
    value: '00',
  },
  {
    label: i18next.t('자동'),
    value: '02',
  },
  {
    label: i18next.t('수동'),
    value: '03',
  },
];

let customized = {};

const MediaStatistics = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [codeId, setCodeId] = useState(1);
  const [userIndex, setUserIndex] = useState(null);
  const [exlFlag, setExlFlag] = useState(initParam.exlFlag);
  const [sortTpCode, setSortTpCode] = useState(initParam.sortTpCode);
  const [page, setPage] = useState(initParam.page);
  const [limit, setLimit] = useState(initParam.limit);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [modifyMemos, setModifyMemos] = useState(false);
  const [modifyScriptNo, setModifyScriptNo] = useState(false);

  // 공통 필터에서 초기값 받아왔는지 확인
  const [getInitParam, setGetInitParam] = useState(false);
  const [params, setParams] = useState(initParam);
  const [prevParams, setPrevParams] = useState(initParam);
  const [userFilterList, setUserFilterList] = useState([]);
  const [loopData, setLoopData] = useState(false);
  const [count, setCount] = useState(0);

  const dispatch = useDispatch();
  const { portal } = useSelector(({ portal }) => ({
    portal: portal,
  }));

  const handlePortalUrl = (url) => (e) => {
    let newPortal = portal.portal;
    let popIndex = newPortal.length;
    newPortal[popIndex] = {
      url: url,
      cnt: popIndex,
    };

    dispatch(setPortal(newPortal));
  };

  const getTpCode = () => {
    targetingCode().then((response) => {
      const tpCodes = response.data.data;

      tpCodes.map((code, tpIndex) => {
        customized[code.CODE_VAL] = {
          makeHead: (headsMap, scriptNo) => {
            const showWindowUrl =
              '/report/daily/par?sDate=' +
              params.sDate +
              '&eDate=' +
              params.eDate +
              '&mediaScriptNo=' +
              scriptNo +
              '&advrtsTpCode=' +
              code.CODE_ID +
              '&uri=/report/daily/par&isPop=1';

            return (
              <IconAreaWrap
                text={getHeadObj(headsMap, code.CODE_VAL).label}
                icon={'faExternalLink'}
                toolTipTitle={t('해당 외부연동의 일자별통계 new 팝업')}
                onClick={handlePortalUrl(showWindowUrl)}
              />
            );
          },
          makeBody: (rowObj, headsMap) => {
            const codeObj = getHeadObj(headsMap, code.CODE_VAL);
            return (
              <>
                {makeRowColumn(
                  rowObj[code.CODE_VAL],
                  codeObj.type,
                  codeObj.format
                )}
              </>
            );
          },
        };

        return {
          label: code.CODE_VAL,
          value: code.CODE_DESC,
        };
      });
    });
  };

  useEffect(() => {
    getAdexUserData();
    getTpCode();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    // 렌더링 후에 실행됨
    // params setting(필터링 혹은 초기 param setting) > 렌더링 > 초기화 값 초기화
    initialization = false;
    if (params !== prevParams) getData();
    setPrevParams({ ...params });
    // eslint-disable-next-line
  }, [params]);

  const getData = () => {
    setLoading(true);
    adexMediaStatistics({
      params,
    })
      .then((response) => {
        const { data } = response.data;
        setLoopData(data.data);
        if (page === 1) setCount(data.count);
        setLoading(false);
        setError(false);
      })
      .catch((error) => {
        setError(true);
        setLoading(false);
      });
  };

  const getAdexUserData = () => {
    getAdexUserList()
      .then((response) => {
        const { data } = response.data;
        data.forEach((adexUser) => {
          customized[adexUser.USER_ID] = {
            makeHead: (headsMap, scriptNo) => {
              const showWindowUrl =
                '/report/daily/par?sDate=' +
                params.sDate +
                '&eDate=' +
                params.eDate +
                '&mediaScriptNo=' +
                scriptNo +
                '&adverId=' +
                adexUser.USER_ID +
                '&uri=/report/daily/par&isPop=1';

              return (
                <IconAreaWrap
                  text={getHeadObj(headsMap, adexUser.USER_ID).label}
                >
                  <MbTooltip
                    // title={t('해당 광고주의 상세 통계')}
                    onClick={handlePortalUrl(showWindowUrl)}
                  >
                    <MbButton iconBtn icon="faExternalLink" />
                  </MbTooltip>
                </IconAreaWrap>
              );
            },
            makeBody: (rowObj, headsMap) => {
              const codeObj = getHeadObj(headsMap, adexUser.USER_ID);
              return (
                <>
                  {makeRowColumn(
                    rowObj[adexUser.USER_ID],
                    codeObj.type,
                    codeObj.format
                  )}
                </>
              );
            },
          };
        });

        setUserFilterList(data);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  /**
   * 유형 구분
   * @param {object} e
   */
  const handleOnCode = (e) => {
    const { value } = e.target;
    const newParams = {
      ...params,
    };

    if (value !== '00') {
      newParams[e.target.name] = value;
    } else {
      newParams[e.target.name] = null;
    }
    if (e.target.name === 'sortTpCode') {
      setSortTpCode(value);
    } else {
      setExlFlag(value);
    }

    setParams(newParams);
  };

  /**
   * selectbox 필터 선택
   * @param {string} name
   * @param {*} value
   */
  const handleFilterChange = (name, index) => {
    if (name === 'advrtsPrdtCode') {
      if (index !== 0) {
        setParams({
          ...params,
          page: 1,
          [name]: filterList[index - 1].CODE_ID,
        });
      }
      setCodeId(index);
    }

    if (name === 'externalId') {
      setParams({
        ...params,
        page: 1,
        [name]: userFilterList[index - 1].USER_ID,
      });
      setUserIndex(index);
    }
    setPage(1);
  };

  /**
   * 필터 초기화
   */
  const handleOnReset = () => {
    initialization = true;
    setParams(initParam);
  };

  /**
   * option 리스트 만들어주는 함수
   * @param {Array} data option list 만들 array
   * @param {string} optionLabelKey option에 나올 label key 값
   * @param {string} optionValueKey option value key 값
   */
  const makeOptionList = (data, optionLabelKey, optionValueKey) => {
    if (typeof data === 'undefined') {
      return [];
    }
    const result = [...data];
    const defaultOption = { [optionLabelKey]: '전체', [optionValueKey]: null };
    if (data.length === 0) {
      result.push(defaultOption);
    } else {
      // 전체 라는 option 추가
      result.unshift(defaultOption);
    }

    return result;
  };

  const adexRowComponent = (row) => {
    const parInfoContent = (parData) => {
      const showWindowUrl =
        '/report/daily/targeting?sDate=' +
        params.sDate +
        '&eDate=' +
        params.eDate +
        '&mediaScriptNo=' +
        parData.MEDIA_SCRIPT_NO +
        '&uri=/report/daily/targeting&isPop=1';

      let defaultMemo = '';
      if (parData.AD_PAR_MEMO !== null) {
        defaultMemo = parData.AD_PAR_MEMO;
      }

      if (modifyMemos !== false) {
        if (typeof modifyMemos[parData.MEDIA_SCRIPT_NO] !== 'undefined') {
          defaultMemo = modifyMemos[parData.MEDIA_SCRIPT_NO];
        }
      }

      let showHistoryWindowUrl =
        '/report/adex/mediaHistory?mediaScriptNo=' +
        parData.MEDIA_SCRIPT_NO +
        '&uri=/report/adex/mediaHistory&isPop=1';

      return (
        <div className={classes.parInfoBox}>
          <div className={classes.corpName}>{parData.CORP_NAME}</div>
          <div className={classes.siteName}>
            <div className={classes.icoBox}>
              <MbButton
                iconBtn
                icon="faExternalLink"
                onClick={handlePortalUrl(showWindowUrl)}
              />
              <p>{parData.SITE_NAME}</p>
            </div>
          </div>
          <div className={classes.accountStr}>{parData.ACCOUNT_STR}</div>
          <div className={classes.scriptNo}>{parData.MEDIA_SCRIPT_NO}</div>
          <div className={classes.memo}>
            <div
              style={{ cursor: 'pointer' }}
              onClick={() =>
                handleToggleModal(true, defaultMemo, parData.MEDIA_SCRIPT_NO)
              }
            >
              {defaultMemo !== '' ? defaultMemo : '[' + t('메모추가') + ']'}
            </div>
          </div>
          <div className={classes.history}>
            <OutlinedButton
              className="mb-product-code-button"
              onClick={handlePortalUrl(showHistoryWindowUrl)}
            >
              {i18next.t('히스토리')}
            </OutlinedButton>
          </div>
        </div>
      );
    };

    const tableContentBasic = (tableData) => {
      const heads = Object.keys(tableData[0]);
      const headList = heads.map((head) => {
        let headLabel;
        let type;
        let format;
        if (head === 'GUBUN') {
          headLabel = '구분';
          type = 'string';
          format = '@';
        } else if (head === 'TOTAL') {
          headLabel = '합계(평균)';
          type = 'number';
          format = '#,##0';
        } else {
          headLabel = head;
          type = 'number';
          format = '#,##0';
        }

        return {
          id: head,
          label: headLabel,
          orderFlag: false,
          type: type,
          format: format,
          displayFlag: true,
        };
      });

      return (
        <BasicTable
          className={classes.root}
          sort="asc"
          order="gubun"
          list={tableData}
          tableKey={row.parData.MEDIA_SCRIPT_NO}
          headCells={makeHeadData(headList)}
          customized={customized}
        />
      );
    };

    return (
      <TableRow key={row.parData.MEDIA_SCRIPT_NO}>
        <TableCell>{parInfoContent(row.parData)}</TableCell>
        <TableCell>
          <div name={row.parData.MEDIA_SCRIPT_NO} style={{ width: 1450 }}>
            {tableContentBasic(row.tableData)}
          </div>
        </TableCell>
      </TableRow>
    );
  };

  const handleToggleModal = (flag, memo, scriptNo) => {
    setIsOpen(flag);
    setModifyScriptNo(scriptNo);
    let memos = { ...modifyMemos };
    memos[scriptNo] = memo;
    if (typeof memo !== 'undefined') setModifyMemos(memos);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage + 1);
    const newParams = { page: newPage + 1 };

    setParams({
      ...params,
      ...newParams,
    });
  };

  const handleChangeRowsPerPage = (event) => {
    setLimit(parseInt(event.target.value, 10));
    setPage(1);

    const newParams = { page: 1 };

    setParams({
      ...params,
      ...newParams,
    });
  };

  const getParam = (newParams) => {
    if (getInitParam === false) {
      initParam = {
        ...initParam,
        ...newParams,
      };
    }
    setGetInitParam(true);

    setParams({
      ...params,
      ...newParams,
    });
  };
  return (
    <>
      <FilterWrap handleOnReset={handleOnReset}>
        <StatisticsCommonFilter
          initialization={initialization}
          noneDisplayFilter={true}
          noneExternalFilter={true}
          getParam={getParam}
          initData={initParam}
        />
        <MbSelectbox
          key="advrtsPrdtCode"
          label={t('상품선택')}
          value={codeId}
          name="advrtsPrdtCode"
          optionData={makeOptionList(filterList, 'CODE_DESC', 'CODE_ID')}
          optionLabelKey="CODE_DESC"
          optionValueKey="CODE_ID"
          handleOnchange={handleFilterChange}
          icon={false}
        />
        {userFilterList.length > 0 && (
          <MbSelectbox
            key="externalId"
            label={t('매체선택')}
            value={userIndex}
            name="externalId"
            optionData={makeOptionList(userFilterList, 'CORP_NAME', 'USER_ID')}
            optionLabelKey="CORP_NAME"
            optionValueKey="USER_ID"
            handleOnchange={handleFilterChange}
            icon={false}
          />
        )}
        <div>
          <AdexSearchBox key={1} params={params} setParams={setParams} />
        </div>
        <Popover
          name="exlFlag"
          label={t('외부연동')}
          // getOnClose={getOnClose}
          selectedLabel={t(
            exlFlag === '00' ? '전체' : exlFlag === 'Y' ? '연동' : '비연동'
          )}
          filtered={exlFlag !== '00'}
          filterType="radio"
          optionSelectedValue={exlFlag}
          optionList={exlFlagList}
          optionOnChange={handleOnCode}
        />
        <Popover
          name="sortTpCode"
          label={t('정렬방식')}
          // getOnClose={getOnClose}
          selectedLabel={t(
            sortTpCode === '00' ? '전체' : sortTpCode === '02' ? '자동' : '수동'
          )}
          filtered={sortTpCode !== '00'}
          filterType="radio"
          optionSelectedValue={sortTpCode}
          optionList={sortTpCodeList}
          optionOnChange={handleOnCode}
        />
      </FilterWrap>

      {loading || error ? (
        <CurrentStatus type={loading ? 'table' : error && 'error'} />
      ) : (
        <div style={{ overflow: 'auto' }}>
          <Table className={classes.table}>
            <TableHead>
              <TableRow>
                <TableCell style={{ width: '20%' }}>{t('지면정보')}</TableCell>
                <TableCell style={{ width: '80%' }}>{t('애드익스')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loopData !== false &&
                loopData.map((row) => {
                  return adexRowComponent(row);
                })}
            </TableBody>
          </Table>
          {loopData !== false && (
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={count}
              rowsPerPage={limit}
              page={page - 1}
              onChangePage={handleChangePage}
              onChangeRowsPerPage={handleChangeRowsPerPage}
            />
          )}
        </div>
      )}
      <MemoModal
        isOpen={isOpen}
        handleToggleModal={handleToggleModal}
        memo={modifyMemos[modifyScriptNo]}
        scriptNo={modifyScriptNo}
        setMemos={setModifyMemos}
      />
    </>
  );
};
export default MediaStatistics;
